/**
 * AmazonStore — "De compras pel Món" public affiliate storefront.
 * Public catalog with category filter + free-text search. Each product opens
 * on Amazon with the affiliate tag `Tarraco21` already applied.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: {
    eyebrow: 'DE COMPRES PEL MÓN',
    title: 'La botiga del Forat Zero',
    subtitle: 'Selecció personal de productes de golf a Amazon. Cada compra amb aquests enllaços m\'ajuda a mantenir el projecte.',
    all: 'Tots',
    search: 'Buscar producte…',
    empty: 'Encara no hi ha productes en aquesta categoria.',
    cta: 'Comprar a Amazon',
    disclaimer: 'Com a afiliat d\'Amazon, guanyo per compres qualificades. Els preus mostrats són orientatius; el preu final el veuràs a Amazon.',
    back: '← Tornar',
  },
  es: {
    eyebrow: 'DE COMPRAS POR EL MUNDO',
    title: 'La tienda del Hoyo Zero',
    subtitle: 'Selección personal de productos de golf en Amazon. Cada compra con estos enlaces me ayuda a mantener el proyecto.',
    all: 'Todos',
    search: 'Buscar producto…',
    empty: 'Aún no hay productos en esta categoría.',
    cta: 'Comprar en Amazon',
    disclaimer: 'Como afiliado de Amazon, gano por compras cualificadas. Los precios mostrados son orientativos; el precio final lo verás en Amazon.',
    back: '← Volver',
  },
  en: {
    eyebrow: 'SHOPPING AROUND THE WORLD',
    title: 'The Hole Zero Shop',
    subtitle: 'A personal selection of golf products on Amazon. Every purchase through these links helps me keep the project alive.',
    all: 'All',
    search: 'Search product…',
    empty: 'No products in this category yet.',
    cta: 'Buy on Amazon',
    disclaimer: 'As an Amazon Affiliate, I earn from qualifying purchases. Displayed prices are indicative; the final price is shown on Amazon.',
    back: '← Back',
  },
  fr: {
    eyebrow: 'SHOPPING AUTOUR DU MONDE',
    title: 'La boutique du Trou Zero',
    subtitle: 'Une sélection personnelle de produits de golf sur Amazon. Chaque achat via ces liens m\'aide à maintenir le projet.',
    all: 'Tous',
    search: 'Chercher un produit…',
    empty: 'Pas encore de produits dans cette catégorie.',
    cta: 'Acheter sur Amazon',
    disclaimer: 'En tant qu\'affilié Amazon, je gagne sur les achats éligibles. Les prix affichés sont indicatifs ; le prix final est visible sur Amazon.',
    back: '← Retour',
  },
};

const pickCopy = (lang) => COPY[(lang || 'ca').split('-')[0]] || COPY.ca;
const pickLabel = (cat, lang) => {
  const key = `label_${(lang || 'ca').split('-')[0]}`;
  return cat[key] || cat.label_ca || cat.slug;
};

export const AmazonStore = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const copy = pickCopy(i18n.language);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/amazon/products`),
      axios.get(`${API_URL}/api/amazon/categories`),
    ])
      .then(([prod, cat]) => {
        setProducts(prod.data || []);
        setCategories((cat.data?.categories || []).filter((c) => c.count > 0));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (filter !== 'all') list = list.filter((p) => p.category === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title?.toLowerCase().includes(q)
          || p.description?.toLowerCase().includes(q)
          || p.subcategory?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [products, filter, search]);

  return (
    <div className="min-h-screen bg-[#0E4D2A] text-[#F2E8D5] pb-24" data-testid="amazon-store-page">
      {/* Vignette + grain (match H0 look) */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Header */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-10 pt-8 md:pt-12">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-[#A8B4A6] hover:text-[#D4AF37] text-sm tracking-[0.2em] font-sans transition-colors"
          data-testid="amazon-back-btn"
        >
          {copy.back}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mt-6"
        >
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">🛒 {copy.eyebrow}</div>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="amazon-store-title">{copy.title}</h1>
          <p className="mt-4 text-[#A8B4A6] italic max-w-2xl mx-auto text-sm md:text-base">{copy.subtitle}</p>
        </motion.div>

        {/* Search bar */}
        <div className="mt-10 flex justify-center">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={copy.search}
            className="w-full max-w-md bg-[#083B1F]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none rounded-full px-5 py-2.5 text-sm placeholder:text-[#A8B4A6]/60 transition-colors"
            data-testid="amazon-search-input"
          />
        </div>

        {/* Category filter chips */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`px-4 py-1.5 rounded-full text-xs tracking-[0.15em] uppercase font-sans border transition-all ${
              filter === 'all'
                ? 'bg-[#D4AF37] text-[#0E4D2A] border-[#D4AF37]'
                : 'text-[#A8B4A6] border-[#A8B4A6]/25 hover:border-[#D4AF37]/60 hover:text-[#F2E8D5]'
            }`}
            data-testid="amazon-chip-all"
          >
            {copy.all} ({products.length})
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setFilter(c.slug)}
              className={`px-4 py-1.5 rounded-full text-xs tracking-[0.15em] uppercase font-sans border transition-all ${
                filter === c.slug
                  ? 'bg-[#D4AF37] text-[#0E4D2A] border-[#D4AF37]'
                  : 'text-[#A8B4A6] border-[#A8B4A6]/25 hover:border-[#D4AF37]/60 hover:text-[#F2E8D5]'
              }`}
              data-testid={`amazon-chip-${c.slug}`}
            >
              {c.emoji} {pickLabel(c, i18n.language)} ({c.count})
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="mt-12" data-testid="amazon-product-grid">
          {loading ? (
            <div className="py-16 text-center text-[#A8B4A6]">…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[#A8B4A6]" data-testid="amazon-empty">{copy.empty}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {filtered.map((p) => (
                <motion.a
                  key={p.id}
                  href={p.amazon_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  whileHover={{ y: -4 }}
                  className="group bg-[#F2E8D5] text-[#0E4D2A] rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col"
                  data-testid={`amazon-card-${p.id}`}
                >
                  <div className="relative aspect-square bg-white overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.style.opacity = 0.25; }}
                    />
                    {p.featured && (
                      <span className="absolute top-2 left-2 bg-[#D4AF37] text-[#0E4D2A] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full">
                        ⭐
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#0E4D2A]/60 font-sans">
                      {p.subcategory || p.category}
                    </div>
                    <div className="mt-1 font-serif text-base leading-snug line-clamp-2 min-h-[3rem]">
                      {p.title}
                    </div>
                    {p.description && (
                      <p className="mt-2 text-xs text-[#0E4D2A]/70 line-clamp-2">{p.description}</p>
                    )}
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      {p.price ? (
                        <span className="font-serif text-lg font-bold text-[#0E4D2A]">{p.price}</span>
                      ) : <span />}
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#0E4D2A] group-hover:text-[#D4AF37] transition-colors font-sans font-medium">
                        {copy.cta} ↗
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="mt-16 text-center text-[10px] tracking-[0.1em] text-[#A8B4A6]/70 font-sans max-w-3xl mx-auto" data-testid="amazon-disclaimer">
          {copy.disclaimer}
        </p>
      </div>
    </div>
  );
};

export default AmazonStore;
